import { Link, NavLink } from 'react-router-dom'
import { Menu, X, Search, Terminal, User, Sun, Moon } from 'lucide-react'
import { useState } from 'react'
import { usePublisherAuth } from '../context/PublisherAuthContext'
import { useTheme } from '../context/ThemeContext'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const { isAuthenticated, publisher } = usePublisherAuth()
  const { theme, toggleTheme } = useTheme()

  const navItems = [
    { to: '/browse', label: 'Browse Skills' },
    { to: '/downloads', label: 'Downloads' },
    { to: '/collections', label: 'Collections' },
    { to: '/docs', label: 'Docs' },
  ]

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200 dark:bg-gray-900 dark:border-gray-800">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
              <Terminal className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gradient">Xarlie</span>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-primary-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </div>

          {/* Search and CTA */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <Link
              to="/browse"
              className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors dark:border-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <Search className="w-4 h-4" />
              <span>Search skills...</span>
              <kbd className="hidden lg:inline-block px-1.5 py-0.5 text-xs bg-gray-100 rounded dark:bg-gray-800">
                /
              </kbd>
            </Link>
            {isAuthenticated ? (
              <Link
                to="/publisher"
                className="flex items-center space-x-2 btn btn-secondary text-sm"
              >
                <User className="w-4 h-4" />
                <span>{publisher?.display_name || 'Dashboard'}</span>
              </Link>
            ) : (
              <>
                <Link to="/publisher/login" className="btn btn-secondary text-sm">
                  Sign In
                </Link>
                <Link to="/publisher/signup" className="btn btn-primary text-sm">
                  Become a Publisher
                </Link>
              </>
            )}
          </div>

          {/* Mobile theme toggle and menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
            <button
              type="button"
              className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100 dark:border-gray-800 animate-fade-in">
            <div className="space-y-2">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `block px-3 py-2 text-base font-medium rounded-lg ${
                      isActive
                        ? 'text-primary-600 bg-primary-50 dark:bg-primary-900/20'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="pt-4 space-y-2">
                {isAuthenticated ? (
                  <Link
                    to="/publisher"
                    className="block btn btn-secondary w-full text-center"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="flex items-center justify-center">
                      <User className="w-4 h-4 mr-2" />
                      Dashboard
                    </span>
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/publisher/login"
                      className="block btn btn-secondary w-full text-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/publisher/signup"
                      className="block btn btn-primary w-full text-center"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Become a Publisher
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  )
}
